import { Injectable } from '@angular/core';
import { Data, Params } from '@angular/router';

import { Observable } from 'rxjs';

import { Navigate, RouterState as NgxsRouterState } from '@ngxs/router-plugin';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';

import { RouterNavStateModel, RouterPath } from './router-nav-state.model';
import { RouterNavStateToken } from './router-nav-state.token';

import { params } from '@app/configurations';
import { DateRanges } from '@app/core/models/common.models';
import { FormatDatesToString, GetDateWithoutTimeZone } from '@app/core/utilities/date.utilities';
import { filterObject } from '@app/core/utilities/object.utilities';
import { stateHasDelay } from '@app/core/utilities/refiners.utilities';
import { differenceInCalendarDays, differenceInDays } from 'date-fns';
import { getRawQueryParams } from '../../../utilities/url.utilities';
import { ChangeCustomer, GoTo, GoToAgencyAdmin, GoToRefresh } from './router-nav-state.actions';

@State({
  name: RouterNavStateToken,
  children: [NgxsRouterState],
})
@Injectable()
export class RouterNavState {
  @Selector([NgxsRouterState])
  static data({ state }: RouterNavStateModel): Data {
    return state?.data || {};
  }

  static dataParam<T = any>(param: string) {
    return createSelector([RouterNavState.data], (p: Params): T => p?.[param]);
  }

  @Selector([NgxsRouterState])
  static params({ state }: RouterNavStateModel): Params {
    return state?.params || {};
  }

  static param(param: string) {
    return createSelector([RouterNavState.params], (p: Params): string => p[param]);
  }

  @Selector([RouterNavState.dateRange])
  static getDays(range: [Date, Date]): number {
    if (!range) return 30; //since default date is last 30 days
    const [start, end] = range;
    return differenceInDays(new Date(end), new Date(start)) + 1;
  }
  @Selector([RouterNavState.queryParams])
  static Dates(qp: Params): DateRanges {
    const [range, compare] = [params.GLOBAL_FILTER_DATE_RANGE, params.GLOBAL_FILTER_DATE_COMPARE].map((d) =>
      !!qp[d] ? qp[d].split(',').map(GetDateWithoutTimeZone) : null,
    );
    return { range, compare };
  }

  @Selector([RouterNavState.queryParams])
  static hasDelay(qp: Params): boolean {
    return stateHasDelay(qp);
  }

  @Selector([RouterNavState.Dates])
  static dateRange({ range }: { range: [Date, Date] }): [Date, Date] {
    return range;
  }

  @Selector([RouterNavState.dateRange])
  static dateRangeLessThan7Days(dateRange?: [Date, Date]): boolean {
    return !!dateRange && differenceInCalendarDays(dateRange[1], dateRange[0]) < 6;
  }

  @Selector([NgxsRouterState])
  static queryParams({ state }: RouterNavStateModel): Params {
    return state?.queryParams || getRawQueryParams() || {};
  }

  @Selector([RouterNavState.queryParam(params.IMPERSONATION_ID)])
  static impersonationId(id): string {
    return id;
  }

  static queryParam(param: string): (qp: Params) => string {
    return createSelector([RouterNavState.queryParams], (qp: Params): string => qp[param]);
  }

  static queryParamMultiple(params: string[]): (qp: Params) => string[] {
    return createSelector([RouterNavState.queryParams], (qp: Params): string[] => params.map((param) => qp[param]));
  }

  static formattedDateQueryParam() {
    return createSelector([RouterNavState.queryParam(params.GLOBAL_FILTER_DATE_RANGE)], (value: string): string =>
      FormatDatesToString(value),
    );
  }

  static queryBooleanParam(param: string, defaultValue = false) {
    return createSelector(
      [RouterNavState.queryParam(param)],
      (value: string): boolean => ({ true: true, false: false })[value] ?? defaultValue,
    );
  }

  static isTestingId(testingId: string) {
    return createSelector(
      [RouterNavState.queryParam(params.TEST_ID)],
      (testId: string): boolean => testId === testingId,
    );
  }

  @Selector([RouterNavState.queryParams])
  static hasEmptyPGFilter(qp: Params): boolean {
    return qp[params.GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY]?.indexOf('campaignsCount:EMPTY') > -1;
  }

  constructor(private store: Store) {}

  @Action(GoToAgencyAdmin)
  goToAgencyAdmin({ dispatch }: StateContext<RouterNavStateModel>, { customer }: GoToAgencyAdmin): Observable<unknown> {
    if (!customer.mainCustomerId) {
      return dispatch(new GoTo(RouterPath.AgencySettings));
    } else {
      return dispatch(new ChangeCustomer(customer.mainCustomerId, RouterPath.AgencySettings));
    }
  }

  @Action(ChangeCustomer)
  changeCustomer(
    { dispatch }: StateContext<RouterNavStateModel>,
    { customerId, path, options }: ChangeCustomer,
  ): Observable<unknown> {
    return dispatch(new GoToRefresh(path, { customerId }, options));
  }

  @Action(GoTo)
  goTo({ dispatch }: StateContext<RouterNavStateModel>, { path, options }: GoTo): Observable<unknown> {
    const realPath =
      path === RouterPath._ && !options.relativeTo
        ? this.store.selectSnapshot(NgxsRouterState.url).split('?')[0]
        : path;

    return dispatch(
      new Navigate([realPath], void 0, {
        queryParamsHandling: 'merge',
        ...(options || {}),
      }),
    );
  }

  @Action(GoToRefresh)
  goToRefresh(_, { path, queryParams, options }: GoToRefresh): void {
    const allQueryParams = {
      ...this.store.selectSnapshot(RouterNavState.queryParams),
      ...(queryParams || {}),
    };

    const customerId = allQueryParams.customerId;

    const tmpParams = filterObject({
      ...queryParams,
      [params.CUSTOMER_ID]: customerId,
      [params.GLOBAL_FILTER_DATE_RANGE]: allQueryParams[params.GLOBAL_FILTER_DATE_RANGE],
      [params.IMPERSONATION_ID]: allQueryParams[params.IMPERSONATION_ID],
    });

    const url = `${location.origin}${path}?${new URLSearchParams(tmpParams).toString()}`.replace(/%3A/g, ':');

    if (options?.newTab) {
      window.open(url, '_blank').focus();
    } else {
      location.href = url;
    }
  }
}
